import { useManagerReport } from "@coaching-culture/api-client";
import {
  ManagerReportItem,
  ProductVisibility,
  UserFilter,
} from "@coaching-culture/types";
import {
  Button,
  DatePill,
  Label,
  Loader,
  Panel,
  PanelHeader,
  SortableTable,
  SortableTableColumn,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { useGoalTerminology } from "auth/OrgProvider";
import CenterColumn from "components/CenterColumn";
import { FilterModal } from "components/FilterPanel";
import { PageHeader } from "components/PageHeader";
import { useProductVisibility } from "hooks/useProductVisibility";
import {
  conversationsStatus,
  daysSinceFeedbackFromAnyone,
  daysSinceFeedbackFromMe,
  daysSinceLastConvo,
  daysToNextConvo,
  feedbackStatus,
  goalsBlocked,
  goalsSet,
  goalsStatus,
  measuresLastReviewed,
  measuresStatus,
  MetricStatus,
  metricStatusLabels,
  nextConvo,
  overdueGoals,
  useTeamRagThresholds,
} from "pages/TeamView/metrics";
import { useMemo, useState } from "react";
import { FaUserFriends } from "react-icons/fa";
import { saveExportAsCsv } from "utils";

const statusRank: Record<MetricStatus, number> = {
  danger: 0,
  warning: 1,
  positive: 2,
};

const StatusPill = ({ status }: { status: MetricStatus }) => (
  <Label size="tiny" color={status} style={{ whiteSpace: "nowrap" }}>
    {metricStatusLabels[status]}
  </Label>
);

const numberCell = (value: number | null) =>
  value == null ? (
    <Text as="span" color="textMuted">
      &mdash;
    </Text>
  ) : (
    value
  );

export const ManagerReport = () => {
  const terms = useGoalTerminology();
  const thresholds = useTeamRagThresholds();
  const hasMeasures =
    useProductVisibility("measures") === ProductVisibility.Enabled;
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });
  const [showFilters, setShowFilters] = useState(false);

  const report = useManagerReport(filter);

  const cols = useMemo((): SortableTableColumn<ManagerReportItem>[] => {
    const cols: SortableTableColumn<ManagerReportItem>[] = [
      {
        name: "manager",
        label: "Manager",
        format: (x) => <UserFlag user={x.manager} />,
        sortFormat: (x) => x.manager.name.toLowerCase(),
        exportFormat: (x) => x.manager.name,
        filter: {
          type: "search",
          getValue: (x) => [x.manager.name, x.manager.email].join(""),
        },
      },
      {
        name: "directReport",
        label: "Direct Report",
        format: (x) => <UserFlag user={x.directReport} />,
        sortFormat: (x) => x.directReport.name.toLowerCase(),
        exportFormat: (x) => x.directReport.name,
        filter: {
          type: "search",
          getValue: (x) => [x.directReport.name, x.directReport.email].join(""),
        },
      },
      {
        name: "goalsStatus",
        label: `${terms.goal.asPluralTitle()} Status`,
        format: (x) => (
          <StatusPill status={goalsStatus(x.directReport, thresholds)} />
        ),
        sortFormat: (x) => statusRank[goalsStatus(x.directReport, thresholds)],
        exportFormat: (x) =>
          metricStatusLabels[goalsStatus(x.directReport, thresholds)],
      },
      {
        name: "goalsSet",
        label: `${terms.goal.asPluralTitle()} Set`,
        format: (x) => goalsSet(x.directReport),
        sortFormat: (x) => goalsSet(x.directReport),
        exportFormat: (x) => goalsSet(x.directReport).toString(),
        numeric: true,
      },
      {
        name: "goalsBlocked",
        label: "Blocked",
        format: (x) => goalsBlocked(x.directReport),
        sortFormat: (x) => goalsBlocked(x.directReport),
        exportFormat: (x) => goalsBlocked(x.directReport).toString(),
        numeric: true,
      },
      {
        name: "goalsOverdue",
        label: "Overdue",
        format: (x) => overdueGoals(x.directReport),
        sortFormat: (x) => overdueGoals(x.directReport),
        exportFormat: (x) => overdueGoals(x.directReport).toString(),
        numeric: true,
      },
      {
        name: "convoStatus",
        label: "Conversations Status",
        format: (x) => (
          <StatusPill
            status={conversationsStatus(x.directReport, thresholds)}
          />
        ),
        sortFormat: (x) =>
          statusRank[conversationsStatus(x.directReport, thresholds)],
        exportFormat: (x) =>
          metricStatusLabels[conversationsStatus(x.directReport, thresholds)],
      },
      {
        name: "daysSinceConvo",
        label: "Days Since Last Conversation",
        format: (x) => numberCell(daysSinceLastConvo(x.directReport)),
        sortFormat: (x) => daysSinceLastConvo(x.directReport) ?? -1,
        exportFormat: (x) =>
          daysSinceLastConvo(x.directReport)?.toString() ?? "",
        numeric: true,
      },
      {
        name: "nextConvo",
        label: "Next Conversation",
        format: (x) => (
          <DatePill
            value={nextConvo(x.directReport)?.scheduledDate}
            color={nextConvo(x.directReport) == null ? "danger" : "positive"}
            dateOnly
          />
        ),
        sortFormat: (x) => nextConvo(x.directReport)?.scheduledDate ?? 0,
        exportFormat: (x) => {
          const nc = nextConvo(x.directReport);
          return nc == null
            ? "Never"
            : new Date(nc.scheduledDate * 1000).toLocaleDateString();
        },
      },
      {
        name: "daysToConvo",
        label: "Days Until Next Conversation",
        format: (x) => numberCell(daysToNextConvo(x.directReport)),
        sortFormat: (x) => daysToNextConvo(x.directReport) ?? -1,
        exportFormat: (x) => daysToNextConvo(x.directReport)?.toString() ?? "",
        numeric: true,
      },
      {
        name: "feedbackStatus",
        label: "Feedback Status",
        format: (x) => (
          <StatusPill status={feedbackStatus(x.directReport, thresholds)} />
        ),
        sortFormat: (x) =>
          statusRank[feedbackStatus(x.directReport, thresholds)],
        exportFormat: (x) =>
          metricStatusLabels[feedbackStatus(x.directReport, thresholds)],
      },
      {
        name: "feedbackFromManager",
        label: "Days Since Feedback From Manager",
        format: (x) =>
          numberCell(daysSinceFeedbackFromMe(x.directReport, x.manager.id)),
        sortFormat: (x) =>
          daysSinceFeedbackFromMe(x.directReport, x.manager.id) ?? -1,
        exportFormat: (x) =>
          daysSinceFeedbackFromMe(x.directReport, x.manager.id)?.toString() ??
          "",
        numeric: true,
      },
      {
        name: "feedbackFromAnyone",
        label: "Days Since Feedback From Anyone",
        format: (x) => numberCell(daysSinceFeedbackFromAnyone(x.directReport)),
        sortFormat: (x) => daysSinceFeedbackFromAnyone(x.directReport) ?? -1,
        exportFormat: (x) =>
          daysSinceFeedbackFromAnyone(x.directReport)?.toString() ?? "",
        numeric: true,
      },
    ];

    if (hasMeasures) {
      cols.push(
        {
          name: "measuresStatus",
          label: "Measures Status",
          format: (x) => (
            <StatusPill status={measuresStatus(x.directReport, thresholds)} />
          ),
          sortFormat: (x) =>
            statusRank[measuresStatus(x.directReport, thresholds)],
          exportFormat: (x) =>
            metricStatusLabels[measuresStatus(x.directReport, thresholds)],
        },
        {
          name: "measuresReviewed",
          label: "Measures Last Reviewed",
          format: (x) => (
            <DatePill
              value={measuresLastReviewed(x.directReport)}
              color={measuresStatus(x.directReport, thresholds)}
              dateOnly
            />
          ),
          sortFormat: (x) => measuresLastReviewed(x.directReport) ?? 0,
          exportFormat: (x) => {
            const mlr = measuresLastReviewed(x.directReport);
            return mlr == null
              ? "Never"
              : new Date(mlr * 1000).toLocaleDateString();
          },
        },
      );
    }

    return cols;
  }, [terms, thresholds, hasMeasures]);

  return (
    <CenterColumn width="100%">
      <PageHeader
        text="Manager Report"
        subtitle="The health of every manager relationship in your organisation"
      >
        <Button color="primary" onClick={() => setShowFilters(true)}>
          Filters ({filter.groups.length + filter.userFields.length})
        </Button>
      </PageHeader>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        onClose={(result) => {
          if (result) {
            setFilter(result);
          }
          setShowFilters(false);
        }}
      />
      <Panel style={{ overflow: "hidden", position: "relative" }}>
        {(report.isLoading || report.isPreviousData) && <Loader overlay />}
        <PanelHeader>
          <Text
            fontSize="title"
            fontWeight={600}
            color="black"
            style={{ flex: 1 }}
          >
            Manager relationships
          </Text>
          {report.data != null && (
            <Text fontSize="small" color="textMuted">
              {report.data.length} relationship
              {report.data.length === 1 ? "" : "s"}
            </Text>
          )}
        </PanelHeader>
        <SortableTable<ManagerReportItem>
          columns={cols}
          data={report.data}
          defaultSort={0}
          paged
          stickyFirstColumn
          onExport={saveExportAsCsv("ManagerReport.csv")}
          emptyMessage="No manager relationships found."
          emptyIcon={FaUserFriends}
        />
      </Panel>
    </CenterColumn>
  );
};
